const COOKIE_SUFFIX = process.env.REACT_APP_ENV !== 'production' ? `-${process.env.REACT_APP_ENV}` : '';
export const EMPION_COOKIE_ID = `_empion-session${COOKIE_SUFFIX}`;
export const AVATAR_COOKIE_ID = `_empion-avatar${COOKIE_SUFFIX}`;

// Supported OAuth providers
export const OAUTH_PROVIDER_LINKEDIN = 'linkedin';

// Roles
export const ROLE_APPLICANT = 'applicant';
export const ROLE_COMPANY_ADMIN = 'company_admin';
export const ROLE_EMPION_ADMIN = 'empion_admin';

// Form modes
export const FORM_MODE_CREATE = 'create';
export const FORM_MODE_EDIT = 'edit';

export const SENIORITY_LEVELS = ['junior', 'mid_level', 'senior', 'lead', 'manager'] as const;

export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

// Supported usege cases for quetionnaires additonal questions
export const QUESTIONNAIRE_SURVEY_CASE_DREAM_EMPLOYER = 'dream_employer';
export const QUESTIONNAIRE_SURVEY_CASE_HAPPINESS_QUESTIONS = 'happiness_questions';
export const QUESTIONNAIRE_SURVEY_CASE_CULTURAL_CATEGORIES = 'cultural_categories';

// Specific survey codes for problematic questionnaires
export const SURVEY_CODES_PROBLEMATIC = [
  'ZPPMku9_oDdBysZ55DW98Q',
  'jndlBMEzxLmYV7CzSYK39Q',
  'QYwdXdouZslaJBoj5iyvcg',
  'm7B9uUY5nTN-QB8SihAeCw',
  'wlKpXp2i0inMPGMZFV7elg',
  'zwXXqIUOmolHeTnslZGkKA',
  'PiTVL3AlC38vlkrHGpuA_w',
];

// Allowed file types for uploads
export const ALLOWED_FILE_TYPES = [
  {
    id: 'cv',
    label: {en: 'CV', de: 'Lebenslauf'},
  },
  {
    id: 'certificate',
    label: {en: 'Certificate', de: 'Zertifikat'},
  },
  {
    id: 'record_of_experience',
    label: {en: 'Record of Experience', de: 'Zeugnis'},
  },
  {
    id: 'diploma',
    label: {en: 'Diploma', de: 'Diplom'},
  },
  {
    id: 'cover_letter',
    label: {en: 'Cover Letter', de: 'Anschreiben'},
  },
  {
    id: 'reference_letter',
    label: {en: 'Reference Letter', de: 'Referenzbrief'},
  },
  {
    id: 'portfolio',
    label: {en: 'Portfolio', de: 'Portfolio'},
  },
  {
    id: 'work_samples',
    label: {en: 'Work Samples', de: 'Arbeitsproben'},
  },
  {
    id: 'other',
    label: {en: 'Other', de: 'Andere'},
  },
];

// Applicant match text
export const applicantMatchText = {
  low: {
    en: 'You have some differences',
    de: 'Ihr seid eher unterschiedlich',
  },
  mid: {
    en: 'High cultural match',
    de: 'Gutes kulturelles Match',
  },
  high: {
    en: 'Perfect match',
    de: 'Perfektes Match',
  },
};

// Company match text
export const companyMatchText = {
  low: {
    en: '& your company are a moderate cultural match.',
    de: '& Dein Unternehmen sind ein moderates kulturelles Match.',
  },
  mid: {
    en: '& your company are a good cultural match!',
    de: '& Dein Unternehmen sind ein gutes kulturelles Match!',
  },
  high: {
    en: '& your company are a perfect cultural match!',
    de: '& Dein Unternehmen sind ein perfektes kulturelles Match!',
  },
};

// Supported work modes
export const SUPPORTED_WORK_MODES = [
  {
    id: 'remote',
  },
  {
    id: 'hybrid',
  },
  {
    id: 'on_site',
  },
];

// Supported employment types
export const SUPPORTED_EMPLOYMENT_TYPES = [
  {
    id: 'full_time',
  },
  {
    id: 'part_time',
  },
  {
    id: 'internship',
  },
];

// Supported degree levels
export const SUPPORTED_DEGREE_LEVELS = [
  {
    id: 'none',
  },
  {
    id: 'high_school',
  },
  {
    id: 'basicSecondaryEducation',
  },
  {
    id: 'midLevelSecondaryEducation',
  },
  {
    id: 'vocationalEducation',
  },
  {
    id: 'bachelors',
  },
  {
    id: 'masters',
  },
  {
    id: 'phd',
  },
];

// Supported languages
export const SUPPORTED_LANGUAGES = [
  {
    id: 'en',
    flag: 'gb',
  },
  {
    id: 'de',
    flag: 'de',
  },
];

// Supported language levels

export const SUPPORTED_LANGUAGE_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Supported gender identities
export const SUPPORTED_GENDER_IDENTITIES = [
  {
    id: 'masculine',
  },
  {
    id: 'feminine',
  },
  {
    id: 'diverse',
  },
  {
    id: 'prefer_not_to_say',
  },
];

// Supported spoken languages
export const SUPPORTED_SPOKEN_LANGUAGES = [
  {
    id: 'en',
  },
  {
    id: 'de',
  },
  {
    id: 'both',
  },
];

export const BIG_CITIES_MAPPING: {[key: string]: {id: string; label: string}[]} = {
  DE: [
    {id: 'berlin', label: 'Berlin'},
    {id: 'hamburg', label: 'Hamburg'},
    {id: 'munich', label: 'Munich'},
    {id: 'cologne', label: 'Cologne'},
    {id: 'frankfurt', label: 'Frankfurt'},
    {id: 'stuttgart', label: 'Stuttgart'},
  ],
  AT: [
    {id: 'vienna', label: 'Vienna'},
    {id: 'graz', label: 'Graz'},
    {id: 'linz', label: 'Linz'},
    {id: 'salzburg', label: 'Salzburg'},
    {id: 'innsbruck', label: 'Innsbruck'},
  ],
  CH: [
    {id: 'zurich', label: 'Zurich'},
    {id: 'geneva', label: 'Geneva'},
    {id: 'basel', label: 'Basel'},
    {id: 'bern', label: 'Bern'},
    {id: 'lausanne', label: 'Lausanne'},
  ],
};

// Supported tags
export const SUPPORTED_TAGS = [
  {
    id: 'directed_to_department',
    label: {
      en: 'Directed to department',
      de: 'An Fachabteilung geleitet',
    },
  },
  {
    id: 'email_sent',
    label: {
      en: 'Email sent',
      de: 'E-Mail gesendet',
    },
  },
  {
    id: 'phone_call_made',
    label: {
      en: 'Phone call made',
      de: 'Telefonat geführt',
    },
  },
  {
    id: 'contacted_without_reply',
    label: {
      en: 'Contacted without reply',
      de: 'Kontaktiert ohne Antwort',
    },
  },
  {
    id: 'first_interview',
    label: {
      en: '1st interview',
      de: '1. Interview',
    },
  },
  {
    id: 'second_interview',
    label: {
      en: '2nd interview',
      de: '2. Interview',
    },
  },
  {
    id: 'third_interview',
    label: {
      en: '3rd interview',
      de: '3. Interview',
    },
  },
  {
    id: 'fourth_interview',
    label: {
      en: '4th interview',
      de: '4. Interview',
    },
  },
  {
    id: 'fifth_interview',
    label: {
      en: '5th interview',
      de: '5. Interview',
    },
  },
  {
    id: 'trial_day',
    label: {
      en: 'Trial day',
      de: 'Probetag',
    },
  },
  {
    id: 'assessment',
    label: {
      en: 'Assessment',
      de: 'Assessment',
    },
  },
  {
    id: 'offer',
    label: {
      en: 'Offer',
      de: 'Angebot',
    },
  },
];

export const PROXIMITY_SEARCH_KM_OPTIONS = [
  {id: 0, label: '0 km'},
  {id: 5, label: '5 km'},
  {id: 10, label: '10 km'},
  {id: 20, label: '20 km'},
  {id: 50, label: '50 km'},
  {id: 100, label: '100 km'},
];

export const SUPPORTED_AGE_GROUPS = [
  {
    id: 'eighteen_or_younger',
    label: {
      en: '18 or younger',
      de: '18 oder jünger',
    },
  },
  {
    id: 'nineteen_to_twenty_five',
    label: {
      en: '19 - 25',
      de: '19 - 25',
    },
  },
  {
    id: 'twenty_six_to_thirty_four',
    label: {
      en: '26 - 34',
      de: '26 - 34',
    },
  },
  {
    id: 'thirty_five_to_forty_four',
    label: {
      en: '35 - 44',
      de: '35 - 44',
    },
  },
  {
    id: 'forty_five_to_fifty_four',
    label: {
      en: '45 - 54',
      de: '45 - 54',
    },
  },
  {
    id: 'fifty_five_to_sixty_four',
    label: {
      en: '55 - 64',
      de: '55 - 64',
    },
  },
  {
    id: 'sixty_five_to_eighty_five',
    label: {
      en: '65 - 85',
      de: '65 - 85',
    },
  },
];

export const TERMS_OF_SERVICE = '20230425';
export const DACH_COUNTRY_CODES = [
  {
    id: 'DE',
    label: {
      de: 'Deutschland',
      en: 'Germany',
    },
  },
  {
    id: 'CH',
    label: {
      de: 'Schweiz',
      en: 'Switzerland',
    },
  },
  {
    id: 'AT',
    label: {
      de: 'Österreich',
      en: 'Austria',
    },
  },
];

// Cultural Analysis status
export const CULTURAL_ANALYSIS_STATUS_INEXISTENT = 'inexistent';
export const CULTURAL_ANALYSIS_STATUS_PENDING = 'pending';
export const CULTURAL_ANALYSIS_STATUS_STARTED = 'started';
export const CULTURAL_ANALYSIS_STATUS_COMPLETED = 'completed';
export const CULTURAL_ANALYSIS_STATUS_RELEASED = 'released';

// Applicant job search status
export const APPLICANT_JOB_SEARCH_STATUS = [
  {
    id: 'actively_looking',
    label: {
      en: 'I am actively looking for a job',
      de: 'Ich bin aktiv auf der Jobsuche',
    },
  },
  {
    id: 'open_to_offers',
    label: {
      en: 'I am just looking around',
      de: 'Ich schaue mich nur um',
    },
  },
  {
    id: 'not_looking',
    label: {
      en: 'I am not looking for a job',
      de: 'Ich bin nicht auf der Suche nach einem Job',
    },
  },
];

export const DateOptions = [
  {
    id: 'years',
    label: {en: 'Years', de: 'Jahre'},
  },
  {
    id: 'months',
    label: {en: 'Months', de: 'Monaten'},
  },
] as const;

export const LEGAL_PAGES_LINKS = {
  en: {
    termsOfService: 'https://empion.io/en/terms-and-conditions',
    termsOfUse: 'https://empion.io/en/terms-of-use',
    privacyPolicy: 'https://empion.io/en/privacy',
  },
  de: {
    termsOfService: 'https://empion.io/agb',
    termsOfUse: 'https://empion.io/nutzungsbedingungen',
    privacyPolicy: 'https://empion.io/datenschutz',
  },
};

export const YEARS_OF_EXPERIENCE = [
  {
    id: 'no_experience',
    label: {
      en: 'No experience',
      de: 'Keine Erfahrung',
    },
  },
  {
    id: 'less_than_1_year',
    label: {
      en: 'Less than 1 year',
      de: 'Weniger als 1 Jahr',
    },
  },
  {
    id: '1_2_years',
    label: {
      en: '1–2 years',
      de: '1–2 Jahre',
    },
  },
  {
    id: '2_3_years',
    label: {
      en: '2–3 years',
      de: '2–3 Jahre',
    },
  },
  {
    id: '3_5_years',
    label: {
      en: '3–5 years',
      de: '3–5 Jahre',
    },
  },
  {
    id: '6_10_years',
    label: {
      en: '6–10 years',
      de: '6–10 Jahre',
    },
  },
  {
    id: '10_15_years',
    label: {
      en: '10–15 years',
      de: '10–15 Jahre',
    },
  },
  {
    id: '15_20_years',
    label: {
      en: '15–20 years',
      de: '15–20 Jahre',
    },
  },
  {
    id: 'more_than_20_years',
    label: {
      en: 'More than 20 years',
      de: 'Mehr als 20 Jahre',
    },
  },
];
